import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useFetchDetail, useFetchDataDetail } from "@hooks/useFetchDetail";
import { PagesID } from "@utils/utilsConfigurations";
import {
  ServicesURL,
  getQueryString,
  downloadFile,
  uploadFile,
} from "@utils/utilsApi";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import dayjs from "dayjs";
import useSWRMutation from "swr/mutation";
import { MethodType, KeyURL } from "src/services/type";
import { fetcher } from "src/services/config";
import ButtonBackPage from "@components/ButtonBackPage";
import { ActionsType, ColumnsType, InputType } from "src/interfaces/Common";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackupTableIcon from "@mui/icons-material/BackupTable";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import MailIcon from "@mui/icons-material/Mail";
import GroupIcon from "@mui/icons-material/Group";
import { createMailWithSubscribed } from "@pages/Contents/Corporate/EditionsPhysical/utils/utils";
import ModalDownloadUploadCst from "@components/ModalDownloadUploadCst";
import { ActionUploadDownloadIterface } from "@components/ModalDownloadUploadCst/types";
import ModalGroupEnrollmentCst from "@components/ModalGroupEnrollmentCst";
import { Visibility } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import UrlLengthAlertDialog from "../EditionsPhysical/utils/UrlLenghtAlertDialog";

export type MyParams = {
  id: string;
};

const DATE_TIME_FORMAT = "DD-MM-YYYY HH:mm";

const PhysicalEditions: React.FC = () => {
  const [idEditionDeleted, setIdEditionDeleted] = useState<string>();
  const [open, setOpen] = useState(false);
  const [urlLengthAlert, setUrlLengthAlert] = useState(false);
  const [openUpload, setOpenUpload] = useState<ActionUploadDownloadIterface>();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
  }>();
  const [openGroupEnrollment, setOpenGroupEnrollment] = useState<{
    open: boolean;
    objectId: string;
    editionId: string;
  }>({ open: false, objectId: "", editionId: "" });
  const navigate = useNavigate();
  const { id } = useParams<keyof MyParams>() as MyParams;
  const { user } = useAuth();
  const { filters } = useFilters();
  const location = useLocation();
  const filterId = `${user?.id_user}_${"physical-edition" + location.state}`;
  const objectLearningId = location?.state?.filters?.id;

  const readOnly =
    (user?.permissions.includes("editions.physical.retrieve") &&
      !user?.permissions.includes("editions.physical.update")) ||
    false;

  const { columns, dataTable, actionsTopToolbar, actionForRow, mutate } =
    useFetchTable(
      location.state
        ? `${ServicesURL.getEditions}?corporateId=${id}&learning_objects_id=${location.state?.filters?.id}`
        : `${ServicesURL.getEditions}?corporateId=${id}&type=PHYSICAL`,
      PagesID["corporate.physical-editions"],
      filters[filterId],
    );

  const filteredActionsTopToolbar = React.useMemo(() => {
    if (!objectLearningId) {
      return actionsTopToolbar?.filter((action) => action.type !== "upload");
    }
    return actionsTopToolbar;
  }, [actionsTopToolbar, objectLearningId]);

  let showIssuedCertificates = true;

  const [editionId, setEditionId] = useState<number | null>(null);

  const fetchUrl = editionId
    ? `${
        ServicesURL.getEnrollEditionDetail
      }?corporateId=${id}${`&editionId=${editionId}`}${
        location?.state?.setGroup && location?.state?.row
          ? `&massiveGroupId=${location?.state?.row[location?.state?.setGroup]}`
          : ``
      }${showIssuedCertificates ? "&showIssuedCertificate=true" : ""}`
    : null;

  const { dataTable: dataTableUsers } = useFetchTable(
    fetchUrl,
    PagesID["enrollment.subscribed.edition"],
    { pageIndex: 0, pageSize: 200 },
  );

  const createAndOpenDeepLink = () => {
    let deepLink = createMailWithSubscribed(
      dataTableUsers?.users,
      dataTable?.output?.find((ed: any) => ed.id === editionId),
    );
    if (deepLink.length > 4000) {
      setUrlLengthAlert(true);
      return;
    }
    window.open(deepLink, "_blank");
  };

  const usersCall = (idEdition: number) => {
    if (idEdition === editionId && dataTableUsers?.users?.length) {
      //Same editionId
      createAndOpenDeepLink();
    } else {
      //Trigger dataTableUsers
      setEditionId(idEdition);
    }
  };

  useEffect(() => {
    if (dataTableUsers?.users?.length) {
      //New editionId
      createAndOpenDeepLink();
    }
  }, [dataTableUsers]);

  const { t } = useTranslation(["physical-editions-page", "modal"]);

  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID.corporate,
  );

  const { dataDetail: nonDeletableIds } = useFetchDataDetail(
    ServicesURL.getEditionsNotDeletable,
  );

  const { trigger: triggerDeleteEdition } = useSWRMutation(
    `${ServicesURL.deletePhysical}`,
    (
      url: string,
      { arg }: { arg: { edition_id: string; corporate_id: string } },
    ) => {
      return fetcher<any>(
        getQueryString(`${url}/${arg.edition_id}`, {
          corporateId: arg.corporate_id,
        }),
        MethodType.DELETE,
      );
    },
  );

  const handleDelete = React.useCallback(async () => {
    if (idEditionDeleted) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteEdition({
          edition_id: idEditionDeleted,
          corporate_id: id,
        });
        const newData = [
          ...dataTable.output.filter((el: any) => el.id !== idEditionDeleted),
        ];
        mutate && mutate({ ...dataTable, ...{ output: newData } });
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdEditionDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    }
  }, [id, dataTable, idEditionDeleted, triggerDeleteEdition]);

  const [columnsMerged, setColumnsMerged] = useState<ColumnsType[]>();

  React.useEffect(() => {
    if (!columnsMerged) {
      if (columns) {
        setColumnsMerged([
          ...(columns.map((el: ColumnsType) => {
            switch (el.accessorKey) {
              case "learning_objects": {
                return {
                  ...el,
                  type: InputType.text,
                  hasFilter: true,
                  Cell: ({ cell }: any) => {
                    const value = cell.getValue();
                    const learning_objects = value && value?.label;
                    return learning_objects;
                  },
                };
              }
              case "learning_objects_id": {
                return {
                  ...el,
                  type: InputType.text,
                  hasFilter: true,
                  Cell: ({ cell }: any) => {
                    const value =
                      cell?.row?.original?.learning_objects &&
                      cell?.row?.original?.learning_objects?.id;
                    return value;
                  },
                };
              }
              case "edition_capacity": {
                return {
                  ...el,
                  type: InputType.text,
                  Cell: ({ cell }: any): React.ReactNode => {
                    const row = cell.row.original;
                    const occupied_places = row.occupied_places ?? 0;
                    const total_places = row.total_places ?? 0;
                    if (!occupied_places && !total_places) return "-";
                    return `${occupied_places}/${total_places}`;
                  },
                };
              }
              case "edition_city": {
                return {
                  ...el,
                  type: InputType.text,
                  Cell: ({ cell }: any) => {
                    const value = cell.getValue();
                    if (!value || value.length === 0) return "-";
                    if (value.length === 1) return value[0];
                    return value.join(", ");
                  },
                };
              }
              default:
                return el;
            }
          }) || []),
        ]);
      }
    }
  }, [columns, columnsMerged]);

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      if (action.type === "edit") {
        return {
          ...action,
          labelTranslate: readOnly ? t("action-view") : t("action-edit"),
          getIcon: () => (readOnly ? <Visibility /> : <EditIcon />),
          callBack: (row: any) =>
            navigate(
              `/corporate/${id}/detail/physical-editions/edit/${row.id}`,
              {
                state: { row: row, readOnly },
              },
            ),
        };
      }
      if (action.type === "button" && action.icon === "subscribed") {
        return {
          ...action,
          getIcon: () => <RecentActorsIcon />,
          callBack: (row: any) => navigate(`${row.id}/subscribed`),
        };
      }
      if (action.key === "virtual-register") {
        return {
          ...action,
          getIcon: () => <BackupTableIcon />,
          callBack: (row: any) => {
            navigate(`${row.id}/virtual-register`, {
              state: location.state,
            });
          },
        };
      }
      if (action.type === "delete") {
        return {
          ...action,
          callBack: (id: string) => {
            setOpen(true);
            setIdEditionDeleted(id);
          },
        };
      }
      if (action.key === "mail") {
        return {
          ...action,
          getIcon: () => <MailIcon />,
          callBack: (row: any) => usersCall(row.id),
        };
      }
      if (action.type === "popper") {
        const mappedActions = action.actions?.map((popperAction: any) => {
          if (popperAction.key === "group-enrollment") {
            return {
              ...popperAction,
              getIcon: () => <GroupIcon />,
              callBack: (row: any) =>
                setOpenGroupEnrollment({
                  open: true,
                  objectId: row.learning_objects?.id,
                  editionId: row.id,
                }),
            };
          }
          if (popperAction.type === "upload") {
            return {
              ...popperAction,
              callBack: (row: any) => {
                const actionModal: ActionUploadDownloadIterface = {
                  ...popperAction,
                  types: ["upload", "download", "template"],
                  disclaimer:
                    t("disclaimer-max-items", {
                      ns: "upload-component",
                      max: 1000,
                    }) || "",
                  onDownload: (type?: string) => {
                    if (type === "template") {
                      return downloadFile(
                        `${ServicesURL.getEditionEnrollmentsTemplate}`,
                      );
                    }
                    return downloadFile(
                      `${ServicesURL.getEditionEnrollmentsDownload}?editionId=${row.id}&corporateId=${id}`,
                    );
                  },
                  onUpload: (formData: FormData) => {
                    return uploadFile(
                      `${ServicesURL.postEditionEnrollmentsUpload}?editionId=${row.id}&corporateId=${id}&objectId=${row.learning_objects?.id}`,
                      formData,
                    );
                  },
                };
                setOpenUpload(actionModal);
              },
            };
          }
          return popperAction;
        });
        return {
          ...action,
          actions: mappedActions,
        };
      }
      return action;
    });
  }, [actionForRow, id]);

  const actionsToolbar: ActionsType[] | undefined = React.useMemo(() => {
    return filteredActionsTopToolbar?.map((action: ActionsType) => {
      switch (action.type) {
        case "upload": {
          const serviceKey = action.service as KeyURL;
          const baseUrl = ServicesURL[serviceKey];

          const actionModal: ActionUploadDownloadIterface = {
            ...action,
            service: baseUrl,
            types: ["upload", "download", "template"],
            disclaimer:
              t("disclaimer-max-items", {
                ns: "upload-component",
                max: 1000,
              }) || "",
            onDownload: (el) => {
              if (el === "template") {
                return downloadFile(
                  `${ServicesURL.getEnrollmentsDownloadTemplate}`,
                );
              }
              return downloadFile(
                `${ServicesURL.getEnrollmentsDownload}?corporateId=${id}&learningObjectId=${objectLearningId}`,
              );
            },
            onUpload: (formData: FormData) => {
              return uploadFile(
                `${ServicesURL.postEnrollmentsUpload}?corporateId=${id}&learningObjectId=${objectLearningId}`,
                formData,
              );
            },
          };
          return {
            ...action,
            callBack: () => setOpenUpload(actionModal),
          };
        }
        case "add": {
          return {
            ...action,
            callBack: () => {
              if (location.state) {
                navigate(`/corporate/${id}/detail/physical-editions/add`, {
                  state: location.state?.row,
                });
              } else {
                navigate(`/corporate/${id}/detail/physical-editions/add`);
              }
            },
          };
        }
        case "popper": {
          const mappedActions = action.actions?.map((popperAction: any) => {
            if (popperAction.key === "group-enrollment") {
              return {
                ...popperAction,
                getIcon: () => <GroupIcon />,
                callBack: (row: any) =>
                  setOpenGroupEnrollment({
                    open: true,
                    objectId: row.learning_objects?.id,
                    editionId: row.id,
                  }),
              };
            }
            return popperAction;
          });
          return {
            ...action,
            actions: mappedActions,
          };
        }
        default:
          return action;
      }
    });
  }, [
    filteredActionsTopToolbar,
    id,
    location.state,
    navigate,
    objectLearningId,
    actionForRow,
  ]);

  //FUNZIONE NECESSARIA PER APPORTARE MODIFICHE AI CAMPI DELLE AZIONI DI RIGA MA IN BASE AL VALORE DEI CAMPI (ROW)
  const customRenderRowActions = ({ row }: { row: any }): React.ReactNode => {
    let configRowNew: any = configurationActionForRow && [
      ...configurationActionForRow,
    ];

    /*
    if (
      !row.original.start_date ||
      dayjs(row.original.start_date, DATE_TIME_FORMAT).isBefore(dayjs())
    ) {
      configRowNew = configRowNew?.map((el: any) => {
        switch (el.type) {
          case "popper":
            return { ...el, disabled: true };
          default:
            return el;
        }
      });
    }
    */

    if (nonDeletableIds?.includes(row.original.id)) {
      configRowNew = configRowNew?.map((el: any) => {
        switch (el.type) {
          case "delete":
            return { ...el, disabled: true };
          default:
            return el;
        }
      });
    }

    return [
      <CustomRowAction
        key={row.id}
        row={row}
        configurationActionForRow={configRowNew}
      />,
    ];
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-corp-editions-physical.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${
            location.state ? location.state?.row?.title : dataDetail?.name || ""
          } - ${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage customRedirect={location.state ? -1 : undefined} />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={"physical-edition" + location.state}
              data={dataTable?.output.map((el: any) => {
                return {
                  ...el,
                };
              })}
              columns={columnsMerged || []}
              configurationTopToolbar={actionsToolbar}
              configurationActionForRow={actionForRow}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.total_elements}
              modalTitle={t("hero-title")?.replace("-", "") || ""}
            />
          </Box>
        )}
      </Container>
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.labelTranslate || ""}
        data={openUpload}
      />
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.labelTranslate || ""}
        data={openUpload}
        onCompleteDisclaimer
      />
      <ModalConfirmsCst
        open={!!open}
        routeToSamePage={true}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        onCallBackClosed={() => {
          setOpen(false);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => {
          setOpen(false);
          handleDelete();
        }}
        status={openToast}
      />
      <ModalGroupEnrollmentCst
        open={openGroupEnrollment.open}
        onClose={() =>
          setOpenGroupEnrollment({ open: false, objectId: "", editionId: "" })
        }
        corporateId={id}
        objectId={openGroupEnrollment.objectId}
        editionId={openGroupEnrollment.editionId}
        onSuccess={() => mutate && mutate()}
        onCompleteDisclaimer
      />
      <ModalDownloadUploadCst
        open={!!openUpload}
        setOpen={() => {
          setOpenUpload(undefined);
        }}
        title={openUpload?.label?.it || "Caricamento Massivo"}
        data={openUpload}
        onCompleteDisclaimer
      />
      <UrlLengthAlertDialog
        open={!!urlLengthAlert}
        onClose={() => setUrlLengthAlert(false)}
      />
    </AnimationFadeIn>
  );
};

export default PhysicalEditions;
